import axios, { AxiosRequestConfig } from 'axios';
import auth0 from '../authentication/auth0Client';
import {
  csiAccountsApiBaseUrl,
  csiVendorAppKey,
  csiVendorKey,
  releaseNotesApiBaseUrl,
} from '../env-variables.config';
import { API_URLS } from '../constants/apiUrl';
import { LOCAL_STORAGE_NAMES } from '../constants/constants';
import { getCookie } from '../constants/utilityFunctions';

const axiosInstance = axios.create({
  baseURL: csiAccountsApiBaseUrl,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    'csi-vendor-key': csiVendorKey,
    'csi-vendor-app-key': csiVendorAppKey,
  },
});
const notificationAxiosInstance = axios.create({
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
});
const releaseNotesAxiosInstance = axios.create({
  baseURL: releaseNotesApiBaseUrl,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    'csi-vendor-key': csiVendorKey,
    'csi-vendor-app-key': csiVendorAppKey,
  },
});

axiosInstance.interceptors.request.use(
  async (config) => {
    const csrfToken =
      getCookie('CSRF-Token-officeaddin') ?? localStorage.getItem(LOCAL_STORAGE_NAMES.csrfToken);
    const excludeOrganisationUrlsForAPI = [API_URLS.ORGANISATION_LIST];
    const organisationData = localStorage.getItem('currentOrganisation');
    const organisationUrl = organisationData
      ? JSON.parse(organisationData)?.organizationApiUrl
      : null;
    config.baseURL = organisationUrl ?? csiAccountsApiBaseUrl;
    const platform = Office.context.diagnostics.platform;
    if (platform === Office.PlatformType.Mac) {
      const token = await auth0.getTokenSilently({ detailedResponse: true });
      config.headers.Authorization = `Bearer ${token?.access_token}`;
      config.withCredentials = false;
    } else {
      config.withCredentials = true;
    }
    if (csrfToken) config.headers['x-csrf-token'] = csrfToken;

    const endpoint = config.url;
    if (endpoint && excludeOrganisationUrlsForAPI.includes(endpoint)) {
      config.baseURL = csiAccountsApiBaseUrl;
    }
    return config;
  },
  (error) => Promise.reject(new Error(error.message || 'Unknown error')),
);

notificationAxiosInstance.interceptors.request.use(
  async (config) => {
    const excludeOrganisationUrlsForAPI = [API_URLS.ORGANISATION_LIST];
    const organisationData = localStorage.getItem(LOCAL_STORAGE_NAMES.currentOrganisation);
    const userData = localStorage.getItem(LOCAL_STORAGE_NAMES.user)
      ? JSON.parse(localStorage.getItem(LOCAL_STORAGE_NAMES.user) || '{}')
      : null;
    const organisationDataParsed = organisationData ? JSON.parse(organisationData) : null;
    const notificationServiceUrl = organisationDataParsed?.notificationServiceApiUrl;
    const organisationUrl = organisationDataParsed?.organizationUrl;
    const strippedUrl = organisationUrl?.replace(/^https?:\/\//, '');
    config.auth = {
      username: strippedUrl ?? '',
      password: userData.id ?? '',
    };
    config.baseURL = `${notificationServiceUrl}/v1`;
    const endpoint = config.url;
    if (endpoint && excludeOrganisationUrlsForAPI.includes(endpoint)) {
      config.baseURL = csiAccountsApiBaseUrl;
    }
    return config;
  },
  (error) => Promise.reject(new Error(error.message || 'Unknown error')),
);
type GetConfig = AxiosRequestConfig & { skipGlobalErrorHandler?: boolean };

// NOTE: callers that opt out of the global handler need the raw axios error so they can read response.status/data
const toApiError = (error: unknown) => {
  const { skipGlobalErrorHandler, skipForbiddenRedirect, handlesVersionConflict } =
    (
      error as {
        config?: {
          skipGlobalErrorHandler?: boolean;
          skipForbiddenRedirect?: boolean;
          handlesVersionConflict?: boolean;
        };
      }
    )?.config ?? {};
  if (skipGlobalErrorHandler || skipForbiddenRedirect || handlesVersionConflict) {
    return error;
  }
  return new Error((error as { message?: string }).message);
};

export const get = async (url: string, params = {}, config: GetConfig = {}) => {
  try {
    const response = await axiosInstance.get(url, { params, ...config });
    return response.data;
  } catch (error) {
    throw toApiError(error);
  }
};

type PostConfig = AxiosRequestConfig & {
  skipGlobalErrorHandler?: boolean;
  skipForbiddenRedirect?: boolean;
  handlesVersionConflict?: boolean;
};

export const post = async (url: string, data = {}, config: PostConfig = {}) => {
  try {
    const isFormData = data instanceof FormData;

    const headers = isFormData
      ? { 'Content-Type': 'multipart/form-data', ...config.headers }
      : { 'Content-Type': 'application/json', ...config.headers };
    const response = await axiosInstance.post(url, data, {
      ...config,
      headers,
    });
    return response.data;
  } catch (error) {
    throw toApiError(error);
  }
};
type PatchConfig = AxiosRequestConfig & {
  skipGlobalErrorHandler?: boolean;
  skipForbiddenRedirect?: boolean;
  handlesVersionConflict?: boolean;
};

export const patch = async (url: string, data = {}, config: PatchConfig = {}) => {
  try {
    const response = await axiosInstance.patch(url, data, config);
    return response.data;
  } catch (error) {
    throw toApiError(error);
  }
};
export const deleteAPI = async (url: string, data = {}, config: PostConfig = {}) => {
  try {
    const response = await axiosInstance.delete(url, { data, ...config });
    return response.data;
  } catch (error) {
    throw toApiError(error);
  }
};
export default axiosInstance;
export const getNotification = async (url: string, params = {}) => {
  try {
    const response = await notificationAxiosInstance.get(url, { params });
    return response.data;
  } catch (error) {
    const errorMessage = (error as { message?: string }).message;
    throw new Error(errorMessage);
  }
};
export const postNotification = async (url: string, data = {}, config: PostConfig = {}) => {
  try {
    const isFormData = data instanceof FormData;

    const headers = isFormData
      ? { 'Content-Type': 'multipart/form-data', ...config.headers }
      : { 'Content-Type': 'application/json', ...config.headers };
    const response = await notificationAxiosInstance.post(url, data, {
      ...config,
      headers,
    });
    return response.data;
  } catch (error) {
    const errorMessage = (error as { message?: string }).message;
    throw new Error(errorMessage);
  }
};
export const getReleaseNotes = async (url: string, params = {}) => {
  try {
    const response = await releaseNotesAxiosInstance.get(url, { params });
    return response.data;
  } catch (error) {
    return {
      error: true,
      message: error instanceof Error ? error.message : 'Unknown error occurred',
    };
  }
};
